import { useState } from 'react';
import { Plus, Search, Edit2, Trash2, TestTube, Phone, Building2, Filter } from 'lucide-react';
import { useLabPatients, useLabClinics, useCreateLabPatient, useUpdateLabPatient, useDeleteLabPatient } from '@/hooks/useLab';
import { LabPatient } from '@/types';
import { LabPatientCreate } from '@/services/lab';

export default function LabPatients() {
  const [search, setSearch] = useState('');
  const [clinicFilter, setClinicFilter] = useState<number | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState<LabPatient | null>(null);
  const [formData, setFormData] = useState<LabPatientCreate>({
    name: '',
    species: 'canino',
    breed: '',
    sex: 'unknown',
    owner_name: '',
    owner_phone: '',
    lab_clinic_id: undefined,
    referring_vet_name: '',
    notes: '',
  });

  const { data: patients = [], isLoading, refetch } = useLabPatients({
    search: search || undefined,
    lab_clinic_id: clinicFilter || undefined,
  });
  const { data: clinics = [] } = useLabClinics({ is_active: true });
  const createMutation = useCreateLabPatient();
  const updateMutation = useUpdateLabPatient();
  const deleteMutation = useDeleteLabPatient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPatient) {
        await updateMutation.mutateAsync({ id: editingPatient.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (patient: LabPatient) => {
    setEditingPatient(patient);
    setFormData({
      name: patient.name,
      species: patient.species,
      breed: patient.breed || '',
      sex: patient.sex,
      owner_name: patient.owner_name || '',
      owner_phone: patient.owner_phone || '',
      lab_clinic_id: patient.lab_clinic_id || undefined,
      referring_vet_name: patient.referring_vet_name || '',
      notes: patient.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (confirm('¿Eliminar este paciente?')) {
      await deleteMutation.mutateAsync(id);
      refetch();
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPatient(null);
    setFormData({
      name: '',
      species: 'canino',
      breed: '',
      sex: 'unknown',
      owner_name: '',
      owner_phone: '',
      lab_clinic_id: undefined,
      referring_vet_name: '',
      notes: '',
    });
  };

  const getClinicName = (clinicId?: number) => {
    if (!clinicId) return null;
    const clinic = clinics.find(c => c.id === clinicId);
    return clinic?.name || null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pacientes de Laboratorio</h1>
          <p className="text-gray-600">Gestionar pacientes externos para analisis</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-lab-600 text-white px-4 py-2 rounded-lg hover:bg-lab-700"
        >
          <Plus className="h-5 w-5" />
          Nuevo Paciente
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o dueno..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={clinicFilter}
              onChange={(e) => setClinicFilter(e.target.value ? parseInt(e.target.value) : '')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
            >
              <option value="">Todas las clinicas</option>
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : patients.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <TestTube className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No hay pacientes registrados</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especie</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dueno</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clinica</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {patients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{patient.name}</div>
                    {patient.breed && <div className="text-sm text-gray-500">{patient.breed}</div>}
                  </td>
                  <td className="px-6 py-4 text-gray-600 capitalize">{patient.species}</td>
                  <td className="px-6 py-4 text-gray-600">{patient.owner_name || '-'}</td>
                  <td className="px-6 py-4">
                    {patient.owner_phone ? (
                      <div className="flex items-center gap-1 text-gray-600">
                        <Phone className="h-4 w-4" />
                        {patient.owner_phone}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4">
                    {(patient.clinic?.name || getClinicName(patient.lab_clinic_id)) ? (
                      <div className="flex items-center gap-1 text-gray-600">
                        <Building2 className="h-4 w-4" />
                        {patient.clinic?.name || getClinicName(patient.lab_clinic_id)}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(patient)}
                        className="p-2 text-lab-600 hover:bg-lab-50 rounded-lg"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(patient.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingPatient ? 'Editar Paciente' : 'Nuevo Paciente'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Clinica selection first */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="inline h-4 w-4 mr-1" />
                  Clinica que deriva
                </label>
                <select
                  value={formData.lab_clinic_id || ''}
                  onChange={(e) => setFormData({ ...formData, lab_clinic_id: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                >
                  <option value="">Sin clinica (particular)</option>
                  {clinics.map((clinic) => (
                    <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Especie *</label>
                  <select
                    required
                    value={formData.species}
                    onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  >
                    <option value="canino">Canino</option>
                    <option value="felino">Felino</option>
                    <option value="equino">Equino</option>
                    <option value="bovino">Bovino</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Raza</label>
                  <input
                    type="text"
                    value={formData.breed}
                    onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
                  <select
                    value={formData.sex}
                    onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  >
                    <option value="unknown">Desconocido</option>
                    <option value="male">Macho</option>
                    <option value="female">Hembra</option>
                  </select>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Datos del Dueno</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                      type="text"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                    <input
                      type="text"
                      value={formData.owner_phone}
                      onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Veterinario que solicita</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del veterinario</label>
                  <input
                    type="text"
                    value={formData.referring_vet_name}
                    onChange={(e) => setFormData({ ...formData, referring_vet_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                    placeholder="Dr. ..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-lab-600 text-white rounded-lg hover:bg-lab-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
